import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { DND_TYPE, Rule, RuleGroup, useRuleStore } from "csi/middleware/attributeRule/RuleStore";
import { DropPlaceholderComponent, customViewFilterPaneDragHandleStyle, DragHandle } from "csi/middleware/customView/data/dnd/data-management-tab-components";
import { ColumnDropDividerWrapper, ColumnDropWrapper, DragWrapper } from "designSystem/dnd/index";
import { Button, FlexColumn, FlexRow, FlexRowCenter, IconButton, SplitButton, Text } from "designSystem/index";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactElement } from "react";
import CustomViewFilterGrid from "./CustomViewFilterGrid";
import CustomViewFilterLeftColumn from "./CustomViewFilterLeftColumn";

interface CustomViewFilterGroupProps {
	ruleGroup: RuleGroup;
}

const customViewFilterGroupDropTypes = [DND_TYPE.RULE];

const CustomViewFilterGroup: FunctionComponent<CustomViewFilterGroupProps> = observer(({ ruleGroup }) => {
	const {
		allConditions,
		canDrop,
		count,
		dropRule,
		overRuleLimit,
		relativeType,
		removeRule,
		selectedCondition,
		setSelectedCondition,
	} = useRuleStore();

	const {
		actions,
		addRule,
		id,
		index,
		isAnyCondition,
		onClickAddFilterButton,
		ruleItems,
	} = ruleGroup;

	function customViewFilterGroupRenderListItem(rule: Rule): ReactElement {
		return (
			<CustomViewFilterGrid rule={rule} ruleGroup={ruleGroup} />
		);
	}

	return (
		<FlexColumn
			data-csi-automation="CustomViewFilterGroup" 
			my={3}
		>
			<FlexRow>
				<CustomViewFilterLeftColumn
					count={count}
					index={index}
					onChange={setSelectedCondition}
					options={allConditions}
					value={selectedCondition}
				/>
				<FlexColumn
					pb={3}
					pt={1}
					px={3}
					sx={{ border: STANDARD_BORDER_STYLE, borderRadius: 3 }}
					width={1}
				>
					<FlexRowCenter
						justifyContent="space-between"
						paddingLeft={2}
						width={1}
					>
						{isAnyCondition ?
							<Text res="CustomViewOrOperatorConditionMessage" />
							:
							<Text res="CustomViewAndOperatorConditionMessage" />
						}
						<IconButton
							design="iconOnly"
							onClick={() => removeRule(index)}
							sx={{ width: 32, height: 32 }}
						>
							<ClearIcon />
						</IconButton>
					</FlexRowCenter>
					<ColumnDropWrapper
						dropTypes={customViewFilterGroupDropTypes}
						onDrop={dropRule}
					>
						<ColumnDropDividerWrapper
							dropPlaceholder={DropPlaceholderComponent}
							hideDraggedItem
							parentId={id}
						>
							{ruleItems.map((rule: Rule, index) => (
								<DragWrapper
									canDrag
									disableBgColorChangeOnDrag
									dragHandle={DragHandle}
									dragHandleStyle={customViewFilterPaneDragHandleStyle}
									dragItem={rule}
									dragType={DND_TYPE.RULE}
									isFullScreen
									key={rule.id}
								>
									{customViewFilterGroupRenderListItem(rule)}
								</DragWrapper>
							))}
						</ColumnDropDividerWrapper>
					</ColumnDropWrapper>
					<FlexRowCenter
						data-csi-automation="CustomViewFilterGroupButtons"  
						mt={2}
					>
						{relativeType ?
							<SplitButton buttons={actions} onClick={onClickAddFilterButton} />
							: (
								<Button
									disabled={overRuleLimit}
									onClick={() => addRule()}
									res="CustomViewAddCustomViewFilter"
									startIcon={<AddIcon />}
								/>
							)}
					</FlexRowCenter>
				</FlexColumn>
			</FlexRow>
		</FlexColumn>
	);
});

export default CustomViewFilterGroup;